import {
  Button,
  CheckboxField,
  ChevronDownIcon,
  Flex,
  PartnerLogo,
  Typography,
} from '@iheartjane/reefer';
import { clsx } from 'clsx';
import { useState } from 'react';
import { useInView } from 'react-intersection-observer';

import { StyledFilterList, StyledFilterListItem } from '../filter.styles';
import type { ConditionOption } from './conditions.types';
import { useConditionSelection } from './conditionSelectionProvider';
import { flattenOptions, optionKey } from './util/conditionSelection.util';

import styles from './conditionOptionList.module.css';

// indexed by depth and clamped, so a value out of range can never reach the DOM
const INDENT_BY_DEPTH = [0, 24, 48, 72, 96] as const;

const indentForDepth = (depth: number) =>
  INDENT_BY_DEPTH[Math.min(Math.max(depth, 0), INDENT_BY_DEPTH.length - 1)];

interface ConditionOptionItemProps {
  depth: number;

  /** Rules the row off from the one above it */
  divided: boolean;

  option: ConditionOption;
}

const ConditionOptionItem = ({
  depth,
  divided,
  option,
}: ConditionOptionItemProps) => {
  const { hasLogos, isSelected, nested, searchQuery, toggleOption } =
    useConditionSelection();
  const [manuallyExpanded, setManuallyExpanded] = useState(false);

  const subItems = nested ? (option.subItems ?? []) : [];
  const hasSubItems = subItems.length > 0;

  // a search-driven expansion wins over local state, so a re-query re-expands
  const expanded = searchQuery.length > 0 || manuallyExpanded;

  const selected = isSelected(option);

  // the row answers for its whole subtree, so anything short of all of it
  // selected reads as partial — including the option itself
  const subtree = [option, ...flattenOptions(subItems)];
  const indeterminate = subtree.some(isSelected) && !subtree.every(isSelected);

  // CheckboxField re-fires onChange from an effect whenever `checked` is driven
  // externally, so only a report that disagrees with ours is a real click
  const handleChange = (checked: boolean) => {
    if (checked !== selected || indeterminate) {
      toggleOption(option);
    }
  };

  const checkbox = (
    <CheckboxField
      label={option.label}
      // the row renders the label, so the field's own copy only has to carry
      // the accessible name
      labelHidden
      // CheckboxField derives the DOM id from name; a label like
      // "Bloom Farms" would otherwise emit an id containing a space
      name={`${option.label.replace(/\s+/g, '-')}-checkbox`}
      checked={selected}
      indeterminate={indeterminate}
      onChange={handleChange}
    />
  );

  return (
    <StyledFilterListItem data-testid={`condition-option-${option.label}`}>
      <span
        className={clsx(styles['item'], {
          [styles['item__divided']]: divided,
          [styles['item__nested']]: nested,
        })}
        onClick={() => toggleOption(option)}
        role="presentation"
        style={{ '--option-indent': `${indentForDepth(depth)}px` }}
      >
        {nested && checkbox}
        <Flex alignItems="center" gap={16}>
          {hasLogos && (
            <PartnerLogo
              name={option.label}
              variant="brand"
              size="xs"
              image={option.logo}
            />
          )}
          <Typography variant="body-semibold">{option.label}</Typography>
        </Flex>
        {!nested && checkbox}
        {hasSubItems && (
          <span className={styles['item_toggle']}>
            <Button.Icon
              icon={<ChevronDownIcon rotate={expanded ? 'down' : 'up'} />}
              ariaLabel={`${expanded ? 'Hide' : 'Show'} ${option.label} sub-items`}
              onClick={(event) => {
                // the row toggles the option, so expanding must not reach it
                event.stopPropagation();
                setManuallyExpanded(!expanded);
              }}
            />
          </span>
        )}
      </span>
      {hasSubItems && expanded && (
        <ConditionOptionList
          depth={depth + 1}
          label={`${option.label}: sub-items`}
          options={subItems}
        />
      )}
    </StyledFilterListItem>
  );
};

export interface ConditionOptionListProps {
  depth?: number;

  label: string;

  /** Fires as the end of the list scrolls into view. Omit for a complete list */
  onEndReached?(): void;

  options: ConditionOption[];
}

/**
 * Asks for the next page as the end of the list comes into view. Keyed on the
 * option count by its parent, so it remounts — and so re-reports being in view
 * — only once a page has actually landed, never while one is in flight.
 */
const EndOfListSentinel = ({ onEndReached }: { onEndReached(): void }) => {
  const { ref } = useInView({
    threshold: 0,
    onChange: (inView) => inView && onEndReached(),
  });

  return <li ref={ref} aria-hidden />;
};

/**
 * One option per row, rendered by data shape: a logo when the section pictures
 * its options, a nested list when the option carries sub-items.
 */
export const ConditionOptionList = ({
  depth = 0,
  label,
  onEndReached,
  options,
}: ConditionOptionListProps) => (
  <StyledFilterList
    aria-label={label}
    className={clsx({ [styles['list__nested']]: depth > 0 })}
    style={{
      '--option-parent-indent': `${indentForDepth(depth - 1)}px`,
    }}
  >
    {options.map((option, index) => (
      <ConditionOptionItem
        key={optionKey(option)}
        depth={depth}
        divided={depth === 0 && index > 0}
        option={option}
      />
    ))}
    {onEndReached && (
      <EndOfListSentinel key={options.length} onEndReached={onEndReached} />
    )}
  </StyledFilterList>
);
